import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", pink:"#F472B6",
  textPri:"#F0F6FF", textSec:"#94A3B8", textMute:"#4B5563",
};

const R=10, U=20, C=220;
const sx = x => C + x*U;
const sy = y => C - y*U;

function svgCoords(e, el) {
  const r = el.getBoundingClientRect();
  const px = ((e.clientX - r.left) / r.width) * 440;
  const py = ((e.clientY - r.top)  / r.height) * 440;
  return {
    x: Math.max(-R, Math.min(R, Math.round((px - C) / U))),
    y: Math.max(-R, Math.min(R, Math.round((C - py) / U))),
  };
}

function describePoint(x, y) {
  if (x===0&&y===0) return "Origin";
  if (x===0) return "On the y-axis";
  if (y===0) return "On the x-axis";
  if (x>0&&y>0) return "Quadrant I";
  if (x<0&&y>0) return "Quadrant II";
  if (x<0&&y<0) return "Quadrant III";
  return "Quadrant IV";
}

function simplify(n) {
  if (n===0) return "0";
  let c=1, rad=n;
  for (let f=2; f*f<=rad; f++) while (rad%(f*f)===0){c*=f; rad/=(f*f);}
  if (rad===1) return String(c);
  return (c===1?"":String(c))+"√"+rad;
}

/* ── Grid ─────────────────────────────────────────────────── */
function CoordGrid({ showQuadrants=false }) {
  const lines=[];
  for (let i=-R;i<=R;i++){
    const maj=i%5===0;
    lines.push(
      <line key={`v${i}`} x1={sx(i)} y1={sy(-R)} x2={sx(i)} y2={sy(R)}
        stroke={maj?"#1E3A5F":"#141E2E"} strokeWidth={maj?.8:.4}/>,
      <line key={`h${i}`} x1={sx(-R)} y1={sy(i)} x2={sx(R)} y2={sy(i)}
        stroke={maj?"#1E3A5F":"#141E2E"} strokeWidth={maj?.8:.4}/>
    );
  }
  return (
    <g>
      {lines}
      <line x1={sx(-R)} y1={C} x2={sx(R)} y2={C} stroke={T.textMute} strokeWidth={1.4}/>
      <line x1={C} y1={sy(-R)} x2={C} y2={sy(R)} stroke={T.textMute} strokeWidth={1.4}/>
      {[-10,-5,5,10].map(v=>(
        <g key={v}>
          <text x={sx(v)} y={C+14} textAnchor="middle" fontSize={9} fill={T.textMute} fontFamily="monospace">{v}</text>
          <text x={C-7} y={sy(v)+3.5} textAnchor="end" fontSize={9} fill={T.textMute} fontFamily="monospace">{v}</text>
        </g>
      ))}
      <text x={sx(R)-4} y={C-9} textAnchor="end" fontSize={10} fill={T.textSec} fontFamily="monospace">x</text>
      <text x={C+8} y={sy(R)+11} fontSize={10} fill={T.textSec} fontFamily="monospace">y</text>
      {showQuadrants && [["I",7.4,9.2],["II",-8.6,9.2],["III",-8.9,-9.4],["IV",7.2,-9.4]].map(([n,qx,qy])=>(
        <text key={n} x={sx(qx)} y={sy(qy)} fontSize={10} fill="#1E3A5F" fontFamily="monospace" letterSpacing={2}>{n}</text>
      ))}
    </g>
  );
}

/* ── Hero plotting board ─────────────────────────────────── */
function HeroBoard() {
  const [pin, setPin] = useState({x:4,y:3,vis:true});
  const svgRef = useRef(null);
  const {x,y,vis} = pin;
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:12}}>
      <svg ref={svgRef} viewBox="0 0 440 440"
        style={{display:"block",width:"100%",cursor:"crosshair",userSelect:"none"}}
        onPointerDown={e=>{if(svgRef.current){const p=svgCoords(e,svgRef.current);setPin({...p,vis:true});}}}>
        <CoordGrid showQuadrants={true}/>
        {vis&&<>
          <line x1={sx(0)} y1={sy(y)} x2={sx(x)} y2={sy(y)} stroke={T.accent} strokeWidth={1} strokeDasharray="3 3" opacity={.7}/>
          <line x1={sx(x)} y1={sy(0)} x2={sx(x)} y2={sy(y)} stroke={T.accent} strokeWidth={1} strokeDasharray="3 3" opacity={.7}/>
          <circle cx={sx(x)} cy={sy(y)} r={9} fill="none" stroke={T.accent} strokeWidth={1.4} opacity={.45}/>
          <circle cx={sx(x)} cy={sy(y)} r={4.2} fill={T.accent}/>
        </>}
      </svg>
      <div style={{display:"flex",alignItems:"baseline",gap:12,borderTop:`1px solid ${T.border}`,marginTop:10,paddingTop:10}}>
        <span style={{fontFamily:"monospace",fontSize:20,fontWeight:700,color:T.textPri}}>({x}, {y})</span>
        <span style={{fontFamily:"monospace",fontSize:12,letterSpacing:".05em",textTransform:"uppercase",color:T.textSec}}>{describePoint(x,y)}</span>
        <button onClick={()=>setPin(p=>({...p,vis:false}))}
          style={{marginLeft:"auto",fontFamily:"monospace",fontSize:11,background:"none",border:`1px solid ${T.border}`,color:T.textMute,padding:"4px 9px",cursor:"pointer",borderRadius:4}}>
          Clear
        </button>
      </div>
      <p style={{fontFamily:"monospace",fontSize:11,color:T.textMute,textAlign:"center",margin:"8px 0 0"}}>
        Tap anywhere on the grid to drop a pin
      </p>
    </div>
  );
}

/* ── Distance explorer ───────────────────────────────────── */
function DistanceExplorer() {
  const [ptA, setPtA] = useState({x:-3,y:1});
  const [ptB, setPtB] = useState({x:4,y:5});
  const dragging = useRef(null);
  const svgRef = useRef(null);

  const getP = e => svgCoords(e, svgRef.current);

  const onPD = e => {
    const p = getP(e);
    const dA = Math.abs(p.x-ptA.x)+Math.abs(p.y-ptA.y);
    const dB = Math.abs(p.x-ptB.x)+Math.abs(p.y-ptB.y);
    dragging.current = dA<=dB?"A":"B";
    if(dragging.current==="A") setPtA(p); else setPtB(p);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPM = e => {
    if(!dragging.current) return;
    const p = getP(e);
    if(dragging.current==="A") setPtA(prev=>(prev.x===p.x&&prev.y===p.y)?prev:p);
    else setPtB(prev=>(prev.x===p.x&&prev.y===p.y)?prev:p);
  };
  const onPU = () => { dragging.current=null; };

  const dx=ptB.x-ptA.x, dy=ptB.y-ptA.y, s=dx*dx+dy*dy;
  const cx2=ptB.x, cy2=ptA.y;

  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 270px",gap:16,marginTop:20,alignItems:"start"}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:12}}>
        <svg ref={svgRef} viewBox="0 0 440 440"
          style={{display:"block",width:"100%",cursor:"crosshair",userSelect:"none"}}
          onPointerDown={onPD} onPointerMove={onPM} onPointerUp={onPU} onPointerCancel={onPU}>
          <CoordGrid/>
          <polygon points={`${sx(ptA.x)},${sy(ptA.y)} ${sx(cx2)},${sy(cy2)} ${sx(ptB.x)},${sy(ptB.y)}`}
            fill={T.accent} fillOpacity={.07}/>
          <line x1={sx(ptA.x)} y1={sy(ptA.y)} x2={sx(cx2)} y2={sy(cy2)} stroke={T.accent} strokeWidth={1.2} strokeDasharray="4 3"/>
          <line x1={sx(cx2)} y1={sy(cy2)} x2={sx(ptB.x)} y2={sy(ptB.y)} stroke={T.accent} strokeWidth={1.2} strokeDasharray="4 3"/>
          <line x1={sx(ptA.x)} y1={sy(ptA.y)} x2={sx(ptB.x)} y2={sy(ptB.y)} stroke={T.orange} strokeWidth={2.2}/>
          <circle cx={sx(ptA.x)} cy={sy(ptA.y)} r={8} fill={T.surface} stroke={T.orange} strokeWidth={2.4} style={{cursor:"grab"}}/>
          <text x={sx(ptA.x)+11} y={sy(ptA.y)-9} fontSize={10.5} fill={T.textSec} fontFamily="monospace">A ({ptA.x}, {ptA.y})</text>
          <circle cx={sx(ptB.x)} cy={sy(ptB.y)} r={8} fill={T.surface} stroke={T.orange} strokeWidth={2.4} style={{cursor:"grab"}}/>
          <text x={sx(ptB.x)+11} y={sy(ptB.y)-9} fontSize={10.5} fill={T.textSec} fontFamily="monospace">B ({ptB.x}, {ptB.y})</text>
        </svg>
        <p style={{fontFamily:"monospace",fontSize:11,color:T.textMute,textAlign:"center",margin:"8px 0 0"}}>Drag point A or B</p>
      </div>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:16}}>
        <div style={{fontSize:11,letterSpacing:.7,textTransform:"uppercase",color:T.textMute,fontWeight:700,marginBottom:12}}>Live Working</div>
        {[["A",`(${ptA.x}, ${ptA.y})`],["B",`(${ptB.x}, ${ptB.y})`],
          ["x₂ − x₁",String(dx)],["y₂ − y₁",String(dy)],
          ["squares",`${dx*dx} + ${dy*dy}`],["sum",String(s)]
        ].map(([label,val])=>(
          <div key={label} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${T.border}`,fontFamily:"monospace",fontSize:13}}>
            <span style={{color:T.textMute}}>{label}</span>
            <span style={{color:T.textPri}}>{val}</span>
          </div>
        ))}
        <div style={{marginTop:12,fontFamily:"monospace",fontSize:18,fontWeight:700,color:T.green}}>
          AB = {simplify(s)}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",marginTop:8,borderTop:`1px solid ${T.border}`,fontFamily:"monospace",fontSize:13}}>
          <span style={{color:T.textMute}}>decimal</span>
          <span style={{color:T.textPri}}>{Math.sqrt(s).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

/* ── UI helpers ──────────────────────────────────────────── */
function Callout({type="info",tag,children}){
  const color=type==="warn"?T.yellow:type==="trick"?T.purple:T.accent;
  return(
    <div style={{background:T.card,borderLeft:`3px solid ${color}`,padding:"14px 18px",
      margin:"18px 0",borderRadius:"0 6px 6px 0",border:`1px solid ${T.border}`,borderLeftWidth:3,borderLeftColor:color}}>
      {tag&&<div style={{fontFamily:"monospace",fontSize:11,letterSpacing:".1em",textTransform:"uppercase",color,marginBottom:6}}>{tag}</div>}
      <div style={{fontSize:14,color:T.textSec,lineHeight:1.65}}>{children}</div>
    </div>
  );
}

function Formula({children}){
  return(
    <div style={{fontFamily:"monospace",fontSize:"clamp(13px,1.8vw,16px)",background:T.card,
      border:`1px dashed ${T.border}`,padding:"15px 18px",textAlign:"center",margin:"16px 0",
      borderRadius:6,color:T.textPri,overflowX:"auto"}}>
      {children}
    </div>
  );
}

function WorkedEx({title,question,steps,answer}){
  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,padding:"16px 20px",borderRadius:8,margin:"18px 0"}}>
      <div style={{fontSize:14,fontWeight:700,color:T.textPri,marginBottom:4}}>{title}</div>
      <div style={{fontSize:13.5,color:T.textSec,fontStyle:"italic",marginBottom:10}}>{question}</div>
      <ol style={{paddingLeft:0,margin:0,listStyle:"none"}}>
        {steps.map((s,i)=>(
          <li key={i} style={{display:"flex",gap:10,margin:"7px 0",fontSize:13.5,color:T.textSec,lineHeight:1.65}}>
            <span style={{flexShrink:0,width:21,height:21,border:`1px solid ${T.border}`,borderRadius:"50%",
              display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"monospace",fontSize:11,color:T.orange,marginTop:2}}>{i+1}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      {answer&&<div style={{borderTop:`1px dashed ${T.border}`,marginTop:10,paddingTop:8,fontWeight:600,fontSize:14,color:T.textPri}}>{answer}</div>}
    </div>
  );
}

function DataTable({headers,rows}){
  return(
    <div style={{overflowX:"auto",margin:"16px 0"}}>
      <table style={{borderCollapse:"collapse",width:"100%",background:T.card,fontSize:13.5}}>
        <thead>
          <tr style={{background:T.surface}}>
            {headers.map((h,i)=>(
              <th key={i} style={{border:`1px solid ${T.border}`,padding:"8px 12px",textAlign:"left",
                color:T.textMute,fontSize:12,letterSpacing:".05em",fontWeight:600}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row,ri)=>(
            <tr key={ri}>
              {row.map((cell,ci)=>(
                <td key={ci} style={{border:`1px solid ${T.border}`,padding:"8px 12px",color:T.textSec}}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PracticeQ({no,marks,question,solution}){
  const [open,setOpen]=useState(false);
  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,marginBottom:10,overflow:"hidden"}}>
      <div onClick={()=>setOpen(!open)}
        style={{cursor:"pointer",padding:"13px 18px",display:"flex",gap:12,alignItems:"flex-start"}}>
        <span style={{fontFamily:"monospace",fontSize:12,color:T.orange,flexShrink:0,paddingTop:2}}>{no}</span>
        <span style={{fontSize:14.5,color:T.textPri,flex:1,lineHeight:1.6}}>{question}</span>
        <span style={{fontFamily:"monospace",fontSize:11,color:T.textMute,border:`1px solid ${T.border}`,
          padding:"2px 7px",borderRadius:4,flexShrink:0}}>{marks}m</span>
        <span style={{color:T.textMute,fontSize:12}}>{open?"▲":"▼"}</span>
      </div>
      {open&&(
        <div style={{padding:"12px 18px 16px 44px",borderTop:`1px dashed ${T.border}`}}>
          <div style={{fontSize:11,letterSpacing:".1em",textTransform:"uppercase",color:T.green,fontWeight:700,marginBottom:8}}>Solution</div>
          <div style={{fontSize:13.5,color:T.textSec,lineHeight:1.7}}>{solution}</div>
        </div>
      )}
    </div>
  );
}

function Cd({children}){
  return<code style={{fontFamily:"monospace",fontSize:".9em",color:T.accent,
    background:"rgba(56,189,248,0.08)",padding:"1px 5px",borderRadius:4}}>{children}</code>;
}
function SL({n}){return<div style={{fontFamily:"monospace",fontSize:11,letterSpacing:".12em",textTransform:"uppercase",color:T.orange,marginBottom:8}}>Topic {String(n).padStart(2,"0")}</div>;}
function H2({c}){return<h2 style={{fontSize:"clamp(22px,3vw,30px)",fontWeight:800,color:T.textPri,margin:"0 0 12px",lineHeight:1.1}}>{c}</h2>;}
function H3({c}){return<h3 style={{fontSize:16,fontWeight:700,color:T.textPri,margin:"22px 0 7px"}}>{c}</h3>;}
function P({c}){return<p style={{fontSize:14.5,color:T.textSec,lineHeight:1.7,maxWidth:"66ch",margin:"9px 0"}}>{c}</p>;}

/* ── Sidebar nav ─────────────────────────────────────────── */
const NAV=[
  {id:"plane",     label:"The Plane",      icon:"📐"},
  {id:"quadrants", label:"Quadrants",      icon:"🗺️"},
  {id:"axes",      label:"Points on Axes", icon:"📍"},
  {id:"distance",  label:"Distance",       icon:"📏"},
  {id:"reflection",label:"Reflection",     icon:"🪞"},
  {id:"midpoint",  label:"Midpoint",       icon:"⚖️"},
  {id:"mistakes",  label:"Mistakes",       icon:"⚠️"},
  {id:"practice",  label:"Practice",       icon:"✏️"},
  {id:"summary",   label:"Summary",        icon:"📋"},
];

const SS={paddingBottom:44,borderBottom:`1px solid #1E2D45`,marginBottom:36};

export default function Class9MathsCh1(){
  const [active,setActive]=useState("plane");
  const mainRef=useRef(null);
  const refs=useRef({});
  const SR=id=>el=>{refs.current[id]=el;};

  function go(id){
    setActive(id);
    const el=refs.current[id], box=mainRef.current;
    if(el&&box) box.scrollTop=el.offsetTop-16;
  }

  return(
    <div style={{display:"flex",height:"calc(100vh - 70px)",background:T.bg,fontFamily:"'Inter',sans-serif"}}>

      {/* Sidebar */}
      <div style={{width:196,flexShrink:0,background:T.surface,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 14px 12px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:T.orange,fontWeight:700,marginBottom:4}}>
            Class 9 · Maths
          </div>
          <div style={{fontSize:13,fontWeight:800,color:T.textPri,lineHeight:1.3}}>Chapter 1</div>
          <div style={{fontSize:11,color:T.textMute,marginTop:3}}>Coordinates</div>
        </div>
        <nav style={{padding:"8px 6px",flex:1,overflowY:"auto"}}>
          {NAV.map(item=>(
            <button key={item.id} onClick={()=>go(item.id)}
              style={{display:"flex",alignItems:"center",gap:8,width:"100%",textAlign:"left",
                padding:"8px 10px",borderRadius:7,border:"none",cursor:"pointer",marginBottom:2,
                background:active===item.id?"rgba(251,146,60,0.1)":"transparent",
                borderLeft:active===item.id?`2px solid ${T.orange}`:"2px solid transparent",
                color:active===item.id?T.orange:T.textSec,
                fontSize:13,fontWeight:active===item.id?700:400,transition:"all .15s"}}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={{padding:"10px 14px",borderTop:`1px solid ${T.border}`,fontSize:11,color:T.textMute,lineHeight:1.65}}>
          Interactive graphs included<br/>Tap questions to reveal
        </div>
      </div>

      {/* Main */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{flexShrink:0,padding:"12px 32px",background:T.surface,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:16}}>
          <span style={{fontSize:11,letterSpacing:".8px",textTransform:"uppercase",color:T.orange,fontWeight:700}}>
            Class 9 Maths · Chapter 1
          </span>
          <span style={{fontSize:13,color:T.textMute}}>Orienting Yourself: The Use of Coordinates</span>
        </div>

        <div ref={mainRef} style={{flex:1,overflowY:"auto",padding:"32px 40px"}}>

          {/* Hero */}
          <div style={{display:"grid",gridTemplateColumns:"1.1fr .9fr",gap:36,alignItems:"start",marginBottom:48}}>
            <div>
              <div style={{fontFamily:"monospace",fontSize:11,letterSpacing:".12em",textTransform:"uppercase",color:T.orange,marginBottom:10}}>
                Orienting Yourself · The Use of Coordinates
              </div>
              <h1 style={{fontSize:"clamp(26px,3.2vw,38px)",fontWeight:900,color:T.textPri,margin:"0 0 12px",lineHeight:1.1}}>
                Two numbers are enough to find <span style={{color:T.accent}}>any</span> point.
              </h1>
              <p style={{fontSize:14.5,color:T.textSec,lineHeight:1.7,maxWidth:"36ch",margin:"0 0 18px"}}>
                Streets in ancient grid-cities were laid so a merchant could count their way to a shop. You are about to learn the same trick, written in algebra.
              </p>
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <button onClick={()=>go("plane")} style={{padding:"9px 18px",borderRadius:4,border:"none",background:T.accent,color:"#000",fontWeight:700,fontSize:13.5,cursor:"pointer"}}>
                  Start the chapter
                </button>
                <button onClick={()=>go("practice")} style={{padding:"9px 18px",borderRadius:4,border:`1px solid ${T.border}`,background:"transparent",color:T.textSec,fontSize:13.5,cursor:"pointer"}}>
                  Jump to practice
                </button>
              </div>
            </div>
            <HeroBoard/>
          </div>

          {/* 01 – The Plane */}
          <div ref={SR("plane")} style={SS}>
            <SL n={1}/><H2 c="The 2-D Cartesian coordinate system"/>
            <P c={<>In earlier classes you used a <strong style={{color:T.textPri}}>number line</strong> — one number, one dimension. We now take two number lines and cross them at right angles. The horizontal one is the <strong style={{color:T.textPri}}>x-axis</strong>, the vertical one the <strong style={{color:T.textPri}}>y-axis</strong>, and where they meet is the <strong style={{color:T.textPri}}>origin O(0, 0)</strong>. The flat surface is the <strong style={{color:T.textPri}}>Cartesian plane</strong>.</>}/>
            <Callout tag="Think of it like this">
              A cinema ticket says "Row H, Seat 14." Two labels pin you down exactly. The Cartesian plane is the same idea, except the labels are numbers and they can go negative.
            </Callout>
            <H3 c="What the two numbers mean"/>
            <ul style={{paddingLeft:20,margin:"8px 0",color:T.textSec,fontSize:14.5,lineHeight:1.8}}>
              <li><strong style={{color:T.textPri}}>x</strong> — perpendicular distance from the <em>y-axis</em>. Right of O is positive, left is negative.</li>
              <li><strong style={{color:T.textPri}}>y</strong> — perpendicular distance from the <em>x-axis</em>. Above O is positive, below is negative.</li>
            </ul>
            <Callout type="warn" tag="Exam alert">
              The <em>x</em>-coordinate is the distance from the <em>y</em>-axis — not from the x-axis. This crossover appears as a 1-mark question every year. Read it twice.
            </Callout>
            <H3 c="Order is not negotiable"/>
            <P c={<><Cd>(x, y)</Cd> is an ordered pair. <Cd>(2, 5)</Cd> is 2 right and 5 up. <Cd>(5, 2)</Cd> is 5 right and 2 up — a completely different spot.</>}/>
            <Formula>If <span style={{color:T.yellow}}>x ≠ y</span> then (x, y) ≠ (y, x) &nbsp;·&nbsp; (x, y) = (y, x) <span style={{color:T.yellow}}>only when x = y</span></Formula>
          </div>

          {/* 02 – Quadrants */}
          <div ref={SR("quadrants")} style={SS}>
            <SL n={2}/><H2 c="The four quadrants and their signs"/>
            <P c="The two axes chop the plane into four regions called quadrants, numbered anticlockwise starting from the top-right."/>
            <DataTable
              headers={["Quadrant","Position","Signs (x, y)","Example"]}
              rows={[["I","Top right","(+, +)","(4, 7)"],["II","Top left","(−, +)","(−5, 3)"],["III","Bottom left","(−, −)","(−2, −6)"],["IV","Bottom right","(+, −)","(3, −5)"]]}
            />
            <Callout type="trick" tag="Trick to remember">
              Quadrant I is fully positive, Quadrant III fully negative — diagonally opposite. Sign of x = left or right; sign of y = up or down. Decide those two and the quadrant names itself.
            </Callout>
          </div>

          {/* 03 – Axes */}
          <div ref={SR("axes")} style={SS}>
            <SL n={3}/><H2 c="Points sitting on the axes"/>
            <P c="A point on an axis is not in any quadrant — quadrants are the open regions between the axes."/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,margin:"16px 0"}}>
              <div>
                <H3 c="On the x-axis"/>
                <P c={<>Height above x-axis is zero. Form: <Cd>(x, 0)</Cd>. E.g. <Cd>(6, 0)</Cd> is 6 right, <Cd>(−6, 0)</Cd> is 6 left.</>}/>
              </div>
              <div>
                <H3 c="On the y-axis"/>
                <P c={<>Distance from y-axis is zero. Form: <Cd>(0, y)</Cd>. E.g. <Cd>(0, 4)</Cd> is 4 above, <Cd>(0, −4)</Cd> is 4 below.</>}/>
              </div>
            </div>
            <Callout type="warn" tag="Exam alert">
              "What is the x-coordinate of every point on the y-axis?" Answer: <strong>0</strong>. The zero always belongs to the axis the point is <em>not</em> lying on.
            </Callout>
            <WorkedEx
              title="Worked example"
              question="Point W has x-coordinate −5. Predict the coordinates of point H on the line through W parallel to the y-axis. Which quadrants can H lie in?"
              steps={[
                "A line parallel to the y-axis is vertical — every point on it keeps the same x-coordinate.",
                <span key="2">So H is of the form <Cd>(−5, y)</Cd> where y can be any number.</span>,
                "x is negative: with y > 0 → Quadrant II; with y < 0 → Quadrant III.",
              ]}
              answer={<span>H = <Cd>(−5, y)</Cd>; lies in Quadrant II or III (or on the x-axis if y = 0).</span>}
            />
          </div>

          {/* 04 – Distance */}
          <div ref={SR("distance")} style={SS}>
            <SL n={4}/><H2 c="Distance between two points"/>
            <P c="Drop two points and join them. Drop a perpendicular from one and a horizontal from the other — you get a right-angled triangle whose hypotenuse is the distance you want."/>
            <Formula>Distance between (x₁, y₁) and (x₂, y₂) &nbsp;=&nbsp; <span style={{color:T.yellow}}>√[ (x₂ − x₁)² + (y₂ − y₁)² ]</span></Formula>
            <P c="Drag either orange point below and watch the triangle and the arithmetic update live."/>
            <DistanceExplorer/>
            <Callout type="trick" tag="Time-saving trick">
              If both x-values are equal the segment is vertical — distance = <Cd>|y₂ − y₁|</Cd>. If both y-values are equal it is horizontal — distance = <Cd>|x₂ − x₁|</Cd>. No squaring needed.
            </Callout>
            <Callout tag="Why signs do not matter">
              You square both differences, so negatives disappear: <Cd>(−4)² = 16 = 4²</Cd>. You can subtract in either order.
            </Callout>
            <WorkedEx
              title="Worked example 1 — clean answer"
              question="Find the distance between A(3, 4) and D(7, 1)."
              steps={[
                <span key="1">Horizontal shift: <Cd>7 − 3 = 4</Cd></span>,
                <span key="2">Vertical shift: <Cd>4 − 1 = 3</Cd></span>,
                <span key="3">Pythagoras: <Cd>AD = √(4² + 3²) = √25</Cd></span>,
              ]}
              answer={<span>AD = <Cd>5 units</Cd></span>}
            />
            <WorkedEx
              title="Worked example 2 — leaving a surd"
              question="Find the distance of (−3, 4) from the origin."
              steps={[
                <span key="1">Origin = <Cd>(0, 0)</Cd></span>,
                <span key="2">Distance = <Cd>√[(−3)² + 4²] = √(9 + 16) = √25</Cd></span>,
              ]}
              answer={<span>Distance = <Cd>5 units</Cd>. Leave irrational answers as surds unless a decimal is asked for.</span>}
            />
          </div>

          {/* 05 – Reflection */}
          <div ref={SR("reflection")} style={SS}>
            <SL n={5}/><H2 c="Reflection in the axes"/>
            <P c="Hold a mirror along an axis. The image lands at the same distance on the other side — exactly one coordinate flips its sign."/>
            <DataTable
              headers={["Mirror","Rule","P(−4, 6) becomes"]}
              rows={[
                ["y-axis (vertical mirror)","(x, y) → (−x, y)","(4, 6)"],
                ["x-axis (horizontal mirror)","(x, y) → (x, −y)","(−4, −6)"],
                ["Origin","(x, y) → (−x, −y)","(4, −6)"],
              ]}
            />
            <Callout type="trick" tag="Trick to remember">
              The mirror you use is the coordinate that <em>survives</em>. Reflect in the y-axis → y stays; reflect in the x-axis → x stays. The other changes sign.
            </Callout>
            <P c={<><strong style={{color:T.textPri}}>Important:</strong> reflection moves a figure but does not stretch it. All side lengths stay exactly the same — only positions change.</>}/>
          </div>

          {/* 06 – Midpoint */}
          <div ref={SR("midpoint")} style={SS}>
            <SL n={6}/><H2 c="Midpoint and collinearity"/>
            <P c="These are the starred (*) problems in the textbook — higher-order questions that usually carry 3 marks."/>
            <H3 c="Midpoint of a segment"/>
            <P c="The midpoint sits exactly halfway, so each coordinate is the average of the two ends."/>
            <Formula>M = ( <span style={{color:T.yellow}}>(x₁ + x₂) / 2</span> ,  <span style={{color:T.yellow}}>(y₁ + y₂) / 2</span> )</Formula>
            <WorkedEx
              title="Worked example — finding a missing endpoint"
              question="M(−7, 1) is the midpoint of A(3, −4) and B(x, y). Find B."
              steps={[
                <span key="1">For x: <Cd>(3 + x)/2 = −7 → x = −17</Cd></span>,
                <span key="2">For y: <Cd>(−4 + y)/2 = 1 → y = 6</Cd></span>,
              ]}
              answer={<span>B = <Cd>(−17, 6)</Cd> &nbsp;·&nbsp; Shortcut: x₂ = 2x_M − x₁</span>}
            />
            <H3 c="Checking collinearity without a graph"/>
            <P c="Three points are collinear when the longest distance equals the sum of the other two."/>
            <WorkedEx
              title="Worked example"
              question="Are M(−3, −4), A(0, 0) and G(6, 8) on the same straight line?"
              steps={[
                <span key="1">MA = √(3² + 4²) = <Cd>5</Cd></span>,
                <span key="2">AG = √(6² + 8²) = √100 = <Cd>10</Cd></span>,
                <span key="3">MG = √(9² + 12²) = √225 = <Cd>15</Cd></span>,
                <span key="4">Check: MA + AG = 5 + 10 = 15 = MG ✓</span>,
              ]}
              answer={<span>Yes — the points are <Cd>collinear</Cd>, A lies between M and G.</span>}
            />
            <Callout tag="Same idea, different question">
              Points lie on a <strong>circle centred at the origin</strong> when their distances from O are all equal — that common distance is the radius.
            </Callout>
          </div>

          {/* 07 – Mistakes */}
          <div ref={SR("mistakes")} style={SS}>
            <SL n={7}/><H2 c="Where marks are usually lost"/>
            <DataTable
              headers={["Mistake","Fix it like this"]}
              rows={[
                ["Writing (y, x) instead of (x, y)","Read horizontal first, vertical second. Left–right before up–down."],
                ["Saying x is distance from the x-axis","x is measured from the y-axis. The letters cross over."],
                ["Claiming (0, 5) is in Quadrant I","It lies on the y-axis. Axis points belong to no quadrant."],
                ["Sign slip: −3 − (−7)","Write fully first: −3 + 7 = 4."],
                ["Forgetting to write 'units'","Distance is a length. Unit marks are free marks."],
                ["Turning √29 into a decimal unasked","Leave surds as surds unless the question asks otherwise."],
                ["Graph drawn without labels","Label both axes, mark the origin, state the scale."],
              ]}
            />
          </div>

          {/* 08 – Practice */}
          <div ref={SR("practice")} style={SS}>
            <SL n={8}/><H2 c="Exam-style practice"/>
            <P c="Attempt each question on paper first, then tap to check the full solution."/>

            <H3 c="Section A — Very short answer (1 mark)"/>
            {[
              {no:"Q1",marks:1,question:"Write the coordinates of the point where the two axes meet.",
                solution:<>The axes meet at the origin. Coordinates: <Cd>(0, 0)</Cd>.</>},
              {no:"Q2",marks:1,question:"In which quadrant does (7, −3) lie?",
                solution:<>x positive, y negative → signs (+, −) → <strong>Quadrant IV</strong>.</>},
              {no:"Q3",marks:1,question:"A point is on the x-axis, 6 units left of the origin. Write its coordinates.",
                solution:<>y = 0 (on x-axis), x = −6 (left). Coordinates: <Cd>(−6, 0)</Cd>.</>},
              {no:"Q4",marks:1,question:"State the signs of the coordinates of a point in Quadrant III.",
                solution:<>Both negative: <Cd>(−, −)</Cd>.</>},
              {no:"Q5",marks:1,question:"If (a, b) = (b, a), what can you conclude about a and b?",
                solution:<>Comparing ordered pairs: a = b. The two numbers must be <strong>equal</strong>.</>},
            ].map(q=><PracticeQ key={q.no} {...q}/>)}

            <H3 c="Section B — Short answer (2 marks)"/>
            {[
              {no:"Q6",marks:2,question:"Find the distance between A(2, 3) and B(6, 6).",
                solution:<><Cd>AB = √[(6−2)² + (6−3)²] = √(16+9) = √25 = 5 units</Cd></>},
              {no:"Q7",marks:2,question:"P(−5, 2) is reflected in the y-axis, then that image is reflected in the x-axis. Write both images.",
                solution:<>Reflect in y-axis: <Cd>P′ = (5, 2)</Cd>. Reflect P′ in x-axis: <Cd>P″ = (5, −2)</Cd>.</>},
              {no:"Q8",marks:2,question:"Find the distance of (−3, 4) from the origin.",
                solution:<><Cd>√[(−3)² + 4²] = √(9+16) = √25 = 5 units</Cd></>},
              {no:"Q9",marks:2,question:"Without using the full distance formula, find the distance between (9, −1) and (−4, −1). Give a reason.",
                solution:<>Both y-values equal −1 → horizontal segment. Distance = <Cd>|−4 − 9| = 13 units</Cd>.</>},
            ].map(q=><PracticeQ key={q.no} {...q}/>)}

            <H3 c="Section C — Long answer (3 marks)"/>
            {[
              {no:"Q10",marks:3,question:"Plot A(0,0), B(5,0), C(5,4), D(0,4). Name figure ABCD and find its area.",
                solution:<>AB = 5, BC = 4, CD = 5, DA = 4. All right angles → <strong>Rectangle</strong>. Area = 5 × 4 = <Cd>20 sq units</Cd>.</>},
              {no:"Q11",marks:3,question:"Check whether M(−3, −4), A(0, 0) and G(6, 8) are collinear, without plotting.",
                solution:<>MA = 5, AG = 10, MG = 15. MA + AG = MG → <strong>collinear</strong>.</>},
              {no:"Q12",marks:3,question:"M(−7, 1) is the midpoint of A(3, −4) and B(x, y). Find B.",
                solution:<><Cd>(3+x)/2 = −7 → x = −17</Cd>; <Cd>(−4+y)/2 = 1 → y = 6</Cd>. B = <Cd>(−17, 6)</Cd>.</>},
            ].map(q=><PracticeQ key={q.no} {...q}/>)}

            <H3 c="Section D — Case study (4 marks)"/>
            <PracticeQ no="Q13" marks={4}
              question="Room corners O(0,0), A(12,0), B(12,10), C(0,10); 1 unit = 1 foot. (i) Length and breadth. (ii) Exact centre. (iii) Distance O to B in surd form. (iv) Wardrobe W₁(3,0) W₂(7,0) W₃(7,2) W₄(3,2) — floor area."
              solution={<div style={{fontSize:13.5,lineHeight:1.8}}>
                <p style={{margin:"4px 0"}}><strong>(i)</strong> OA = 12 ft, OC = 10 ft → 12 ft × 10 ft room.</p>
                <p style={{margin:"4px 0"}}><strong>(ii)</strong> Midpoint of OB = <Cd>((0+12)/2, (0+10)/2) = (6, 5)</Cd>.</p>
                <p style={{margin:"4px 0"}}><strong>(iii)</strong> <Cd>OB = √(144+100) = √244 = 2√61 ≈ 15.62 ft</Cd>.</p>
                <p style={{margin:"4px 0"}}><strong>(iv)</strong> Width = 4 ft, depth = 2 ft → <Cd>8 sq ft</Cd>.</p>
              </div>}
            />
          </div>

          {/* 09 – Summary */}
          <div ref={SR("summary")} style={{paddingBottom:48}}>
            <SL n={9}/><H2 c="One-page revision sheet"/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:16}}>
              <div>
                <H3 c="Definitions"/>
                <ul style={{paddingLeft:20,color:T.textSec,fontSize:14,lineHeight:1.9}}>
                  <li>Two perpendicular lines locate any point in a plane.</li>
                  <li>Horizontal = <strong style={{color:T.textPri}}>x-axis</strong>; vertical = <strong style={{color:T.textPri}}>y-axis</strong>.</li>
                  <li>Meeting point = <strong style={{color:T.textPri}}>origin (0, 0)</strong>.</li>
                  <li>Four <strong style={{color:T.textPri}}>quadrants</strong>, numbered anticlockwise from top-right.</li>
                </ul>
              </div>
              <div>
                <H3 c="Instant recall"/>
                <ul style={{paddingLeft:20,color:T.textSec,fontSize:14,lineHeight:1.9}}>
                  <li>x-coord = distance from <em>y</em>-axis; y-coord = distance from <em>x</em>-axis.</li>
                  <li>x-axis points: <Cd>(x, 0)</Cd>. y-axis points: <Cd>(0, y)</Cd>.</li>
                  <li>Signs: I(+,+) · II(−,+) · III(−,−) · IV(+,−)</li>
                  <li>Horiz. dist = <Cd>|x₂−x₁|</Cd>; vert. = <Cd>|y₂−y₁|</Cd></li>
                </ul>
              </div>
            </div>
            <Formula>
              Distance = <span style={{color:T.yellow}}>√[(x₂−x₁)² + (y₂−y₁)²]</span>
              &nbsp;&nbsp;·&nbsp;&nbsp;
              Midpoint = <span style={{color:T.yellow}}>((x₁+x₂)/2, (y₁+y₂)/2)</span>
            </Formula>
          </div>

        </div>
      </div>
    </div>
  );
}
